import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, User, Calendar, HelpCircle, LogOut, ChevronDown, Check, Settings, Home, Building } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { useFavorites } from "@/hooks/useFavorites";
import { useProviders } from "@/hooks/useProviders";
import { useProviderAccess } from "@/hooks/useProviderAccess";
import { useDomainProvider } from "@/hooks/useDomainProvider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const DashboardHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { favorites, setAsFavorite, isSettingFavorite } = useFavorites();
  const { data: providers = [] } = useProviders();
  const { data: providerAccess } = useProviderAccess();
  const { isDomainLocked, lockedProvider } = useDomainProvider();
  const [providerSwitcherOpen, setProviderSwitcherOpen] = useState(false);
  const hasAutoSetFavorite = useRef(false);
  
  // Get favorite provider from user's favorites
  const favoriteProvider = favorites.length > 0 ? favorites[0]?.providers : null;
  
  // On locked domains, use the locked provider for branding; otherwise use favorite
  const headerProvider = isDomainLocked && lockedProvider ? lockedProvider : favoriteProvider;
  
  // Auto-correct favorite when on a locked domain to prevent mismatch
  useEffect(() => {
    if (
      isDomainLocked && 
      lockedProvider && 
      favoriteProvider?.id !== lockedProvider.id && 
      !hasAutoSetFavorite.current &&
      !isSettingFavorite
    ) {
      hasAutoSetFavorite.current = true;
      setAsFavorite(lockedProvider.id);
    }
  }, [isDomainLocked, lockedProvider, favoriteProvider?.id, setAsFavorite, isSettingFavorite]);

  const showBackToDashboard = location.pathname.startsWith('/booking/') && location.pathname !== '/booking';

  const handleBackToDashboard = () => {
    if (location.pathname.includes('/booking/') && location.pathname.split('/').length === 4) {
      navigate(-1);
    } else {
      navigate("/dashboard");
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/auth");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleSelectProvider = (providerId: string) => {
    setAsFavorite(providerId);
    setProviderSwitcherOpen(false);
  };

  const userName = user?.user_metadata?.first_name && user?.user_metadata?.last_name
    ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
    : user?.email || "User";
  
  const userEmail = user?.email || "";

  // Get branding from header provider (locked provider on custom domains, favorite otherwise)
  const primaryColor = (headerProvider as any)?.brand_primary_color || undefined;
  const secondaryColor = (headerProvider as any)?.brand_secondary_color || undefined;
  const brandFont = (headerProvider as any)?.brand_font || undefined;
  const headerTextColor = (headerProvider as any)?.brand_header_text_color || undefined;
  const iconTextColor = (headerProvider as any)?.brand_header_icon_text_color || undefined;
  const displayName = (headerProvider as any)?.display_name || headerProvider?.name;
  const letterSpacing = (headerProvider as any)?.brand_header_letter_spacing || undefined;
  const hasBranding = !!primaryColor;

  // Determine text color based on background
  const getContrastColor = (hexColor: string | undefined) => {
    if (!hexColor) return undefined;
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  // Use custom header text color if defined, otherwise fall back to auto-contrast
  const textColor = headerTextColor || getContrastColor(primaryColor);

  return (
    <header 
      className="sticky top-0 left-0 right-0 z-50 border-b"
      style={{ 
        backgroundColor: primaryColor || 'hsl(var(--background))',
        borderColor: hasBranding ? 'transparent' : 'hsl(var(--border))',
        fontFamily: brandFont
      }}
    >
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex justify-between items-center py-3">
          <div className="flex items-center space-x-4">
            {/* Provider header - show switcher only if not domain locked */}
            {isDomainLocked ? (
              // When domain is locked, show the locked provider branding without switcher
              <div className="flex items-center gap-2">
                {headerProvider ? (
                  <>
                    {headerProvider.image_url ? (
                      <img 
                        src={headerProvider.image_url}
                        alt={headerProvider.name} 
                        className="h-8 w-8 rounded-lg object-cover"
                      />
                    ) : (
                      <div 
                        className="h-8 w-8 rounded-lg flex items-center justify-center"
                        style={{ 
                          backgroundColor: secondaryColor || (hasBranding ? 'rgba(255,255,255,0.2)' : 'hsl(var(--muted))'),
                          color: textColor || 'hsl(var(--foreground))'
                        }}
                      >
                        <span className="text-sm font-semibold">
                          {headerProvider.name?.charAt(0)}
                        </span>
                      </div>
                    )}
                    <span 
                      className="font-semibold text-sm sm:text-base truncate max-w-[150px] sm:max-w-none"
                      style={{ 
                        color: textColor || 'hsl(var(--foreground))',
                        letterSpacing: letterSpacing || undefined
                      }}
                    >
                      {displayName}
                    </span>
                  </>
                ) : (
                  <span 
                    className="text-sm font-medium"
                    style={{ color: textColor || 'hsl(var(--foreground))' }}
                  >
                    Wellsuites
                  </span>
                )}
              </div>
            ) : (
              // Normal provider switcher when not domain locked
              <Popover open={providerSwitcherOpen} onOpenChange={setProviderSwitcherOpen}>
                <PopoverTrigger asChild>
                  <button 
                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                    disabled={isSettingFavorite}
                  >
                    {favoriteProvider ? (
                      <>
                        {favoriteProvider.image_url ? (
                          <img 
                            src={favoriteProvider.image_url}
                            alt={favoriteProvider.name} 
                            className="h-8 w-8 rounded-lg object-cover"
                          />
                        ) : (
                          <div 
                            className="h-8 w-8 rounded-lg flex items-center justify-center"
                            style={{ 
                              backgroundColor: secondaryColor || (hasBranding ? 'rgba(255,255,255,0.2)' : 'hsl(var(--muted))'),
                              color: textColor || 'hsl(var(--foreground))'
                            }}
                          >
                            <span className="text-sm font-semibold">
                              {favoriteProvider.name?.charAt(0)}
                            </span>
                          </div>
                        )}
                        <span 
                          className="font-semibold text-sm sm:text-base truncate max-w-[150px] sm:max-w-none"
                          style={{ 
                            color: textColor || 'hsl(var(--foreground))',
                            letterSpacing: letterSpacing || undefined
                          }}
                        >
                          {displayName}
                        </span>
                        <ChevronDown 
                          className="h-4 w-4" 
                          style={{ color: textColor || 'hsl(var(--muted-foreground))' }}
                        />
                      </>
                    ) : (
                      <span 
                        className="text-sm font-medium"
                        style={{ color: textColor || 'hsl(var(--foreground))' }}
                      >
                        Select a provider
                        <ChevronDown className="h-4 w-4 inline ml-1" />
                      </span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-2" align="start">
                  <div className="text-xs font-medium text-muted-foreground px-2 py-1.5 mb-1">
                    Switch provider
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-0.5">
                    {providers.map((provider) => (
                      <button
                        key={provider.id}
                        onClick={() => handleSelectProvider(provider.id)}
                        className="w-full flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted text-left transition-colors"
                      >
                        {provider.image_url ? (
                          <img 
                            src={provider.image_url}
                            alt={provider.name} 
                            className="h-8 w-8 rounded-lg object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-semibold text-muted-foreground">
                              {provider.name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{provider.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{provider.location}</p>
                        </div>
                        {favoriteProvider?.id === provider.id && (
                          <Check className="h-4 w-4 text-primary flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}

            {showBackToDashboard && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleBackToDashboard}
                style={{ color: textColor }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {providerAccess && (
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="font-medium rounded-full hover:bg-black/10"
                style={{ color: textColor }}
              >
                <Link to="/management">
                  <Settings className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Management</span>
                </Link>
              </Button>
            )}
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="h-9 w-9 rounded-full p-0"
                    style={{ color: textColor }}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.user_metadata?.avatar_url} alt={userName} />
                      <AvatarFallback 
                        className="font-semibold text-xs"
                        style={{
                          backgroundColor: secondaryColor || 'hsl(var(--foreground))',
                          color: iconTextColor || textColor || 'hsl(var(--background))'
                        }}
                      >
                        {userName.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64 bg-card shadow-xl border border-border rounded-xl" align="end">
                  <div className="px-4 py-3 border-b border-border">
                    <p className="font-semibold text-foreground">{userName}</p>
                    <p className="text-sm text-muted-foreground">{userEmail}</p>
                  </div>
                  
                  <DropdownMenuItem className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer py-3">
                    <Link to="/" className="w-full flex items-center">
                      <Home className="h-4 w-4 mr-3 text-gray-500" />
                      Home
                    </Link>
                  </DropdownMenuItem>
                  
                  {providerAccess && (
                    <DropdownMenuItem className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer py-3 sm:hidden">
                      <Link to="/management" className="w-full flex items-center">
                        <Settings className="h-4 w-4 mr-3 text-gray-500" />
                        Management
                      </Link>
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuItem className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer py-3">
                    <Link to="/profile" className="w-full flex items-center">
                      <User className="h-4 w-4 mr-3 text-gray-500" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  
                  {!providerAccess && (
                    <DropdownMenuItem className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer py-3">
                      <Link to="/dashboard" className="w-full flex items-center">
                        <Calendar className="h-4 w-4 mr-3 text-gray-500" />
                        Bookings
                      </Link>
                    </DropdownMenuItem>
                  )}
                  
                  {/* Show link to locked provider profile when on custom domain */}
                  {isDomainLocked && lockedProvider && (
                    <DropdownMenuItem className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer py-3">
                      <Link to={`/provider/${lockedProvider.slug}`} className="w-full flex items-center">
                        <Building className="h-4 w-4 mr-3 text-gray-500" />
                        View {lockedProvider.name}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuItem className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer py-3">
                    <Link to="/support" className="w-full flex items-center">
                      <HelpCircle className="h-4 w-4 mr-3 text-gray-500" />
                      Support
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem 
                    className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer py-3"
                    onClick={handleSignOut}
                  >
                    <LogOut className="h-4 w-4 mr-3 text-gray-500" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button 
                  asChild 
                  variant="ghost" 
                  size="sm" 
                  className="font-medium rounded-full"
                  style={{ color: textColor }}
                >
                  <Link to="/auth">Log in</Link>
                </Button>
                <Button 
                  asChild 
                  size="sm" 
                  className="rounded-full font-medium"
                  style={{ 
                    backgroundColor: secondaryColor || (hasBranding ? 'rgba(255,255,255,0.9)' : undefined),
                    color: hasBranding ? primaryColor : undefined
                  }}
                >
                  <Link to="/auth">Sign up</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;